import React, { FC, useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { GameProgressResponse } from "@models/game";

const WEEKDAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"]; // Sun=0 .. Sat=6

export interface WeeklyActivityBarProps {
  progress: GameProgressResponse | null | undefined;
}

const WeeklyActivityBar: FC<WeeklyActivityBarProps> = ({ progress }) => {
  const days = useMemo(() => {
    const trend = progress?.trend ?? [];
    const trendDates = new Set(
      trend.map((e) => e.date?.slice(0, 10)).filter(Boolean),
    );

    const result: { date: string; isToday: boolean; active: boolean; dayInitial: string }[] = [];
    const now = new Date();

    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().slice(0, 10);
      result.push({
        date: dateStr,
        isToday: i === 0,
        active: trendDates.has(dateStr),
        dayInitial: WEEKDAY_INITIALS[d.getDay()],
      });
    }
    return result;
  }, [progress?.trend]);

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      {days.map((day) => (
        <Stack key={day.date} alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: day.active ? "primary.main" : "action.hover",
              border: day.isToday ? 2 : 0,
              borderColor: "primary.main",
              boxSizing: "border-box",
              ...(day.isToday && {
                animation: "pulse-ring 2s ease-out infinite",
                "@keyframes pulse-ring": {
                  "0%": { boxShadow: "0 0 0 0 rgba(25, 118, 210, 0.3)" },
                  "100%": { boxShadow: "0 0 0 8px rgba(25, 118, 210, 0)" },
                },
              }),
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
            {day.dayInitial}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

export default WeeklyActivityBar;
